import { baseApi } from 'services/baseApi';
import { AppConstants } from 'constants/index';
import qs from 'qs';
import { Report } from 'types';
import {
  ChangeReleaseNoteStatusArg,
  DeleteReleaseNoteArg,
  EditReleaseNoteArg,
  ReleaseNote,
  ReleaseNoteItem,
  ReleaseNoteListItem,
  ReleaseNoteRequest,
  ReleaseNotesParams,
} from 'features/releaseNote/types';
import { cacheTags } from 'constants/cacheTags';
import { getMessage } from 'utils/helpers';
import { i18n } from 'services';

export const releaseNoteApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getReleaseNotes: builder.query<Report<ReleaseNoteListItem>, ReleaseNotesParams>({
      query: (params) => {
        const { DataCollector, ReleaseNotes } = AppConstants.api;
        return {
          url: [DataCollector, ReleaseNotes],
          params,
          paramsSerializer: (param) => qs.stringify(param),
        };
      },
      providesTags: [cacheTags.ReleaseNotes],
    }),
    getReleaseNote: builder.query<ReleaseNote, number>({
      query: (id) => {
        const { DataCollector, ReleaseNotes } = AppConstants.api;
        return {
          url: [DataCollector, ReleaseNotes, id],
        };
      },
      providesTags: [cacheTags.ReleaseNote],
    }),
    deleteReleaseNote: builder.mutation<void, DeleteReleaseNoteArg>({
      query: ({ id, name }) => {
        const { DataCollector, ReleaseNotes } = AppConstants.api;
        return {
          method: AppConstants.methods.DELETE,
          url: [DataCollector, ReleaseNotes, id],
          meta: {
            successMessage: getMessage(i18n.t('tool:lRule'), i18n.t('notification:slDeleted'), name),
          },
        };
      },
      invalidatesTags: [cacheTags.ReleaseNotes],
    }),
    changeReleaseNoteStatus: builder.mutation<void, ChangeReleaseNoteStatusArg>({
      query: ({ id, name, isEnabled }) => {
        const { DataCollector, ReleaseNotes, Status } = AppConstants.api;
        return {
          method: AppConstants.methods.PUT,
          url: [DataCollector, ReleaseNotes, id, Status],
          data: { name, isEnabled },
          meta: {
            successMessage: getMessage(i18n.t('tool:note'), i18n.t(isEnabled ? 'notification:slEnabled' : 'notification:slDisabled'), name),
          },
        };
      },
      invalidatesTags: [cacheTags.ReleaseNotes],
    }),
    createReleaseNote: builder.mutation<void, ReleaseNoteRequest>({
      query: (data) => {
        const { DataCollector, ReleaseNotes } = AppConstants.api;
        return {
          method: AppConstants.methods.POST,
          url: [DataCollector, ReleaseNotes],
          data,
          meta: {
            successMessage: getMessage(i18n.t('tool:package'), i18n.t('notification:slSent'), data.name),
          },
        };
      },
      invalidatesTags: [cacheTags.ReleaseNote, cacheTags.ReleaseNotes],
    }),
    editReleaseNote: builder.mutation<void, EditReleaseNoteArg>({
      query: ({ id, data }) => {
        const { DataCollector, ReleaseNotes } = AppConstants.api;
        return {
          method: AppConstants.methods.PUT,
          url: [DataCollector, ReleaseNotes, id],
          data,
          meta: {
            successMessage: getMessage(i18n.t('tool:package'), i18n.t('notification:slSent'), data.name),
          },
        };
      },
      invalidatesTags: [cacheTags.ReleaseNote, cacheTags.ReleaseNotes],
    }),
    markAsSeenReleaseNote: builder.mutation<void, number>({
      query: (id) => {
        const { DataCollector, ReleaseNotes, Seen } = AppConstants.api;
        return {
          method: AppConstants.methods.PUT,
          url: [DataCollector, ReleaseNotes, id, Seen],
        };
      },
      invalidatesTags: [cacheTags.LatestReleaseNote],
    }),
    getLatestReleaseNote: builder.query<ReleaseNoteItem, void>({
      query: () => {
        const { DataCollector, ReleaseNotes, Latest } = AppConstants.api;
        return {
          url: [DataCollector, ReleaseNotes, Latest],
        };
      },
      providesTags: [cacheTags.LatestReleaseNote],
    }),
    getResourceReleaseNotes: builder.query<ReleaseNoteItem[], void>({
      query: () => {
        const { ReportingAdmin, Resource, ReleaseNotes } = AppConstants.api;
        return {
          url: [ReportingAdmin, Resource, ReleaseNotes],
        };
      },
    }),
  }),
});

export const {
  useGetReleaseNotesQuery, useDeleteReleaseNoteMutation, useChangeReleaseNoteStatusMutation,
  useGetReleaseNoteQuery, useGetLatestReleaseNoteQuery, useMarkAsSeenReleaseNoteMutation, useGetResourceReleaseNotesQuery,
  useCreateReleaseNoteMutation, useEditReleaseNoteMutation,
} = releaseNoteApi;
